<script lang="ts">
  /**
   * Innstillinger for en blomsterkasse — brukes både til Å OPPRETTE en ny kasse
   * (kasse = null) og REDIGERE en eksisterende. Vises i et bunn-ark (Sheet).
   * Redigerbart: navn, emoji, notat, hvilke sensorer kassa har og (for en eksisterende
   * kasse) kalibreringen av jordsensorene. Ved opprettelse i tillegg potte_id (må matche
   * ESP32-ens POTTE_ID) + skillevegger. Sletting i rediger.
   */
  import { supabase } from '../lib/supabase';
  import { potter, user, pottePlanter, loadPottePlanter } from '../lib/stores';
  import { visFeil, visOk } from '../lib/toast';
  import { startNyRunde, slettKasse } from '../lib/plantinger';
  import { sensorOppsett, sensorListe, harNoenSensor, type SensorOppsett } from '../lib/sensorer';
  import {
    sensorEtikett, formaterTidssiden, kasseForm, bakSeksjon, JORD_TORR, JORD_VAT, type KasseForm,
  } from '../lib/utils';
  import { jordKalibreringFeil } from '../lib/jordkalibrering';
  import type { Potte } from '../lib/database.types';
  import TestSimulator from './TestSimulator.svelte';

  type Avlesning = {
    registrert_at: string | null;
    jord1: number | null;
    jord2: number | null;
    jord3: number | null;
    jord4: number | null;
  };

  let {
    kasse,
    sisteAvlesning = null,
    onLukk,
    onLagret,
  }: {
    kasse: Potte | null;
    /** Siste ekte avlesning (rå ADC), til jordkalibreringen. Sensorer kassa ikke har, er null. */
    sisteAvlesning?: Avlesning | null;
    onLukk: () => void;
    onLagret: () => void;
  } = $props();

  // Skjemaet kopierer kasse-feltene ÉN gang ved åpning (komponenten remountes
  // hver gang arket åpnes via {#if} i forelderen), så engangs-kopi er riktig.
  // k0 = plain const (ikke-reaktiv) → unngår «state_referenced_locally».
  // svelte-ignore state_referenced_locally
  const k0 = kasse;
  const erNy = k0 === null;
  let potteId = $state(k0?.potte_id ?? '');
  let navn = $state(k0?.navn ?? '');
  let emoji = $state(k0?.emoji ?? '🪴');
  let notater = $state(k0?.notater ?? '');
  // Ny kasse: alle sensorer valgt. Kolonnen `sensorer` kan mangle i databasen
  // (docs/sql/2026-09-23-sensorer.sql); da er valget avledet av har_sensorer.
  let valg = $state<SensorOppsett>(sensorOppsett(k0 ?? { har_sensorer: true }));
  let skille = $state<boolean[]>(k0 ? [...k0.skillevegger] : [false, false]);
  // To potter (1.0) eller fire kopper på rad (2.0 stor). Kolonnen kom 21.10.2026.
  let form = $state<KasseForm>(kasseForm(k0));
  const FORMER = [
    { id: 'potte', tekst: 'To potter (1.0)' },
    { id: 'stor', tekst: 'Fire kopper på rad (stor)' },
  ] as const;
  /** Kolonnen `form` finnes ikke ennå (docs/sql/2026-10-21-potter-form.sql). */
  const manglerFormKolonne = (e: { message: string } | null) => !!e && /'form'|potter\.form\b/.test(e.message);
  const FORM_SQL_MELDING =
    'Resten er lagret, men formen krever en ny kolonne i databasen. Kjør docs/sql/2026-10-21-potter-form.sql i Supabase.';

  const SENSOR_VALG = [
    { navn: 'vann', tekst: '🌊 Vannstand' },
    { navn: 'vann2', tekst: '🌊 Høyre skuff (laser 2)' },
    { navn: 'klima', tekst: '🌡️ Temp og luftfukt' },
    { navn: 'kamera', tekst: '📷 Kamera' },
  ] as const;

  /** Kolonnen `sensorer` finnes ikke ennå: Postgres svarer 42703 og nevner den. */
  const manglerSensorKolonne = (e: { message: string } | null) => !!e && /sensorer/.test(e.message);
  const SQL_MELDING =
    'Resten er lagret, men sensorvalget krever én ny kolonne i databasen. Kjør docs/sql/2026-09-23-sensorer.sql i Supabase.';
  /** Kolonnene til jordkalibreringen kom 20.10.2026 (docs/sql/2026-10-20-…). */
  const manglerJordKolonne = (e: { message: string } | null) => !!e && /jord_(torr|vaat)_adc/.test(e.message);
  const JORD_SQL_MELDING =
    'Resten er lagret, men jordkalibreringen krever nye kolonner i databasen. Kjør docs/sql/2026-10-20-stor-to-skuffer-og-jordkalibrering.sql i Supabase.';

  // ---- Jordkalibrering per kasse ----
  // Rå ADC for tørr og våt jord. Tomt felt = de felles tallene. Tekstfelt, så et tomt
  // felt betyr «felles» og ikke 0.
  let torrTekst = $state(k0?.jord_torr_adc != null ? String(k0.jord_torr_adc) : '');
  let vaatTekst = $state(k0?.jord_vaat_adc != null ? String(k0.jord_vaat_adc) : '');
  const tolk = (s: string): number | null => (s.trim() === '' ? null : Number(s.trim()));
  const torrNy = $derived(tolk(torrTekst));
  const vaatNy = $derived(tolk(vaatTekst));
  const jordFeil = $derived(
    torrNy === null && vaatNy === null ? null : jordKalibreringFeil(torrNy ?? JORD_TORR, vaatNy ?? JORD_VAT),
  );
  // Siste råverdier fra probene kassa har, og snittet av dem (knappene under feltene).
  const raaNaa = $derived(
    sisteAvlesning
      ? [sisteAvlesning.jord1, sisteAvlesning.jord2, sisteAvlesning.jord3, sisteAvlesning.jord4]
          .map((v, i) => ({ nr: i + 1, v }))
          .filter((x): x is { nr: number; v: number } => x.v !== null)
      : [],
  );
  const snittNaa = $derived(raaNaa.length ? Math.round(raaNaa.reduce((a, x) => a + x.v, 0) / raaNaa.length) : null);
  let lagrer = $state(false);
  let bekreftSlett = $state(false);

  const hurtigEmoji = ['🪴', '🌿', '🌱', '🥬', '🍅', '🌺', '🌶️', '🍓'];

  async function lagre() {
    if (lagrer) return;
    if (!navn.trim()) {
      visFeil('Gi kassa et navn.');
      return;
    }
    if (erNy && !/^[a-z0-9_-]+$/i.test(potteId.trim())) {
      visFeil('potte_id må fylles ut (bokstaver/tall, må matche ESP32-en).');
      return;
    }
    if (!erNy && jordFeil) {
      visFeil(`Jordkalibreringen: ${jordFeil}`);
      return;
    }
    lagrer = true;
    try {
    const sensorFelt = { har_sensorer: harNoenSensor(valg), sensorer: sensorListe(valg) };
    let utenSensorKolonne = false;
    if (erNy) {
      const ny = {
        potte_id: potteId.trim(),
        navn: navn.trim(),
        emoji: emoji.trim() || '🪴',
        notater: notater.trim() || null,
        skillevegger: skille,
        i_drift: false,
        owner_id: $user?.id ?? null,
      };
      // Formen sendes bare for stor; 'potte' er standard i databasen.
      let rad: Partial<Potte> = { ...ny, ...sensorFelt, ...(form === 'stor' ? { form } : {}) };
      let utenFormKolonne = false;
      let { data, error } = await supabase.from('potter').insert(rad).select().single();
      // Mangler en ny kolonne i databasen, lagres resten uten den (én om gangen).
      for (let forsok = 0; forsok < 2 && error; forsok++) {
        if (manglerFormKolonne(error) && 'form' in rad) {
          utenFormKolonne = true;
          const { form: _f, ...uten } = rad;
          rad = uten;
        } else if (manglerSensorKolonne(error) && 'sensorer' in rad) {
          utenSensorKolonne = true;
          const { sensorer: _s, ...uten } = rad;
          rad = uten;
        } else break;
        ({ data, error } = await supabase.from('potter').insert(rad).select().single());
      }
      lagrer = false;
      if (error) {
        visFeil(
          /duplicate|unique/i.test(error.message)
            ? `Det finnes allerede en kasse med potte_id «${potteId.trim()}».`
            : 'Kunne ikke opprette kassa — sjekk nettet og prøv igjen.',
        );
        return;
      }
      if (data) potter.update((l) => [...l, data as Potte]);
      if (utenSensorKolonne) visFeil(SQL_MELDING);
      else if (utenFormKolonne) visFeil(FORM_SQL_MELDING);
      else visOk('Blomsterkasse opprettet');
    } else {
      let oppd: Partial<Potte> = {
        navn: navn.trim(),
        emoji: emoji.trim() || '🪴',
        notater: notater.trim() || null,
        ...sensorFelt,
      };
      // Jordkalibreringen sendes bare når den er endret, så resten kan lagres også før
      // kolonnene finnes i databasen.
      if (torrNy !== (k0?.jord_torr_adc ?? null) || vaatNy !== (k0?.jord_vaat_adc ?? null)) {
        oppd = { ...oppd, jord_torr_adc: torrNy, jord_vaat_adc: vaatNy };
      }
      // Formen sendes også bare når den er endret. Tilbake til to potter: plantene i
      // kopp 2 og 4 står i de bakre plassene, så de pottene må ha skillevegg for å synes.
      if (form !== kasseForm(k0)) {
        oppd = { ...oppd, form };
        if (form === 'potte') {
          const her = $pottePlanter[kasse!.potte_id] ?? [];
          const nye = skille.map((delt, i) => delt || her.some((p) => p.seksjon === bakSeksjon(i)));
          if (nye.some((d, i) => d !== skille[i])) oppd = { ...oppd, skillevegger: nye };
        }
      }
      let utenJordKolonne = false;
      let utenFormKolonne = false;
      let { error } = await supabase.from('potter').update(oppd).eq('id', kasse!.id).select('id').single();
      // Mangler en ny kolonne i databasen, lagres resten uten den (én om gangen).
      for (let forsok = 0; forsok < 3 && error; forsok++) {
        if (manglerFormKolonne(error) && 'form' in oppd) {
          utenFormKolonne = true;
          const { form: _f, skillevegger: _s, ...uten } = oppd;
          oppd = uten;
        } else if (manglerSensorKolonne(error) && 'sensorer' in oppd) {
          utenSensorKolonne = true;
          const { sensorer: _s, ...uten } = oppd;
          oppd = uten;
        } else if (manglerJordKolonne(error) && 'jord_torr_adc' in oppd) {
          utenJordKolonne = true;
          const { jord_torr_adc: _t, jord_vaat_adc: _v, ...uten } = oppd;
          oppd = uten;
        } else break;
        ({ error } = await supabase.from('potter').update(oppd).eq('id', kasse!.id).select('id').single());
      }
      lagrer = false;
      if (error) {
        visFeil('Kunne ikke lagre endringene — sjekk nettet og prøv igjen.');
        return;
      }
      potter.update((l) => l.map((p) => (p.id === kasse!.id ? { ...p, ...oppd } : p)));
      if (utenSensorKolonne) visFeil(SQL_MELDING);
      else if (utenJordKolonne) visFeil(JORD_SQL_MELDING);
      else if (utenFormKolonne) visFeil(FORM_SQL_MELDING);
      else visOk('Lagret');
    }
    onLagret();
    onLukk();
    } catch {
      visFeil('Lagringen kunne ikke bekreftes. Sjekk nettet og last inn kassa på nytt.');
    } finally {
      lagrer = false;
    }
  }

  // ---- Drift eller testmodus ----
  // Begge retninger krever et nytt trykk som sier hva som skjer. Byttet endrer
  // bare modusen: ingen datoer nullstilles og ingenting slettes. Å starte
  // plantene på nytt er en egen handling (Start ny runde, under).
  let iDrift = $state(k0?.i_drift ?? false);
  let bekreftModus = $state(false);
  let byttermodus = $state(false);

  $effect(() => {
    if (!bekreftModus) return;
    const t = setTimeout(() => (bekreftModus = false), 6000);
    return () => clearTimeout(t);
  });

  async function byttModus() {
    if (!kasse || byttermodus) return;
    const ny = !iDrift;
    byttermodus = true;
    try {
      const { error } = await supabase.from('potter').update({ i_drift: ny }).eq('id', kasse.id).select('id').single();
      if (error) {
        visFeil('Kunne ikke bytte modus — sjekk nettet og prøv igjen.');
        return;
      }
      potter.update((l) => l.map((p) => (p.id === kasse.id ? { ...p, i_drift: ny } : p)));
      iDrift = ny;
      visOk(ny ? 'Kassa er i drift' : 'Kassa er i testmodus');
    } catch {
      visFeil('Modusbyttet kunne ikke bekreftes. Last inn kassa på nytt før du prøver igjen.');
    } finally {
      byttermodus = false;
      bekreftModus = false;
    }
  }

  // ---- Start ny runde ----
  // Setter plantedatoene til i dag. I drift legges den gamle runden i
  // historikken. I testmodus gjenbrukes plantingene med ny dato og runde.
  // En ny runde starter også en ny bildeperiode uten å slette gamle bilder.
  const antallPlanter = $derived(
    kasse ? ($pottePlanter[kasse.potte_id] ?? []).length : 0,
  );
  let bekreftRunde = $state(false);
  let starterRunde = $state(false);

  $effect(() => {
    if (!bekreftRunde) return;
    const t = setTimeout(() => (bekreftRunde = false), 6000);
    return () => clearTimeout(t);
  });

  async function nyRunde() {
    if (!kasse || starterRunde) return;
    const id = kasse.potte_id;
    starterRunde = true;
    try {
      const r = await startNyRunde(id, iDrift);
      await loadPottePlanter(id);
      onLagret();
      if (!r.ok) visFeil(r.melding);
      else visOk('Ny runde startet — bildene begynner på nytt og plantedatoene er i dag');
    } catch {
      visFeil('Kunne ikke oppdatere visningen. Last inn kassa på nytt før du prøver igjen.');
    } finally {
      starterRunde = false;
      bekreftRunde = false;
    }
  }

  async function slett() {
    if (!kasse || lagrer) return;
    const id = kasse.id;
    lagrer = true;
    try {
      if (!await slettKasse(id)) {
        visFeil('Kunne ikke bekrefte slettingen. Last inn appen på nytt før du prøver igjen.');
        return;
      }
      potter.update((l) => l.filter((p) => p.id !== id));
      visOk('Blomsterkasse slettet');
      onLagret();
      onLukk();
    } finally {
      lagrer = false;
    }
  }
</script>

<div class="font-display text-[25px] font-semibold leading-[1.05]">
  {erNy ? 'Ny blomsterkasse' : 'Innstillinger'}
</div>
<div class="font-mono text-[11px] text-text-muted mt-1">
  {erNy ? 'Sett opp en ny kasse' : potteId}
</div>

<div class="flex flex-col gap-4 mt-5">
  {#if erNy}
    <div>
      <label for="potteid" class="label block mb-1.5">potte_id</label>
      <input id="potteid" bind:value={potteId} placeholder="f.eks. potte2" class="input font-mono" />
      <p class="text-[11px] text-text-dim mt-1">Må være lik <code>POTTE_ID</code> i ESP32-ens config.py.</p>
    </div>
  {/if}

  <div>
    <label for="navn" class="label block mb-1.5">Navn</label>
    <input id="navn" bind:value={navn} placeholder="f.eks. Kjøkkenkassa" class="input" />
  </div>

  <div>
    <span class="label block mb-1.5">Emoji</span>
    <div class="flex items-center gap-2">
      <input bind:value={emoji} maxlength="4" class="input !w-16 text-center text-xl" aria-label="Emoji" />
      <div class="flex gap-1 flex-wrap">
        {#each hurtigEmoji as e (e)}
          <button
            class="w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-colors {emoji ===
            e
              ? 'bg-leaf/15 border border-leaf/30'
              : 'bg-surface-raised border border-border hover:bg-surface-hover'}"
            onclick={() => (emoji = e)}
            aria-pressed={emoji === e}
          >
            {e}
          </button>
        {/each}
      </div>
    </div>
  </div>

  <div>
    <label for="notat" class="label block mb-1.5">Notat (valgfritt)</label>
    <textarea id="notat" bind:value={notater} rows="2" placeholder="f.eks. står på kjøkkenbenken" class="input resize-none"></textarea>
  </div>

  <!-- Formen: to åttekantede potter (1.0) eller fire kopper på rad (2.0 stor). -->
  <div>
    <span class="label block mb-1.5">Form</span>
    <div class="flex flex-wrap gap-1.5" role="group" aria-label="Kassas form">
      {#each FORMER as f (f.id)}
        <button
          class="text-xs px-2.5 py-2 rounded-lg border transition-colors {form === f.id
            ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
            : 'bg-surface-raised text-text-muted border-border'}"
          onclick={() => (form = f.id)}
          aria-pressed={form === f.id}
        >
          {f.tekst}
        </button>
      {/each}
    </div>
    {#if form === 'stor'}
      <p class="text-[11px] text-text-dim mt-1.5 leading-snug">
        Kopp 1–4 fra venstre er plass 1–4, og skjermen på kassa viser jordfukten på rad.
      </p>
    {/if}
  </div>

  <!-- Sensorene kassa har. Det som ikke er valgt, vises ikke og gir ingen varsler. -->
  <div>
    <span class="label block mb-1">Sensorer</span>
    <p class="text-[11px] text-text-dim mb-2 leading-snug">
      Trykk av det som finnes. Resten vises ikke i appen og gir ingen varsler.
    </p>
    <div class="text-[12px] text-text-muted mb-1.5">💧 Jordfukt, velg plassene som er koblet til. ESP32 oppdateres innen ca. 30 sekunder.</div>
    <div class="grid grid-cols-2 gap-1.5">
      {#each [0, 1, 2, 3] as i (i)}
        <button
          class="text-xs px-2.5 py-2 rounded-lg border text-left transition-colors {valg.jord[i]
            ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
            : 'bg-surface-raised text-text-muted border-border'}"
          onclick={() => (valg.jord[i] = !valg.jord[i])}
          aria-pressed={valg.jord[i]}
        >
          {#if form === 'stor'}
            {sensorEtikett(i + 1, skille, form)}
          {:else}
            <span class="font-mono">{i + 1}</span> · {sensorEtikett(i + 1, skille)}
          {/if}
        </button>
      {/each}
    </div>
    <div class="flex flex-wrap gap-1.5 mt-2">
      {#each SENSOR_VALG as s (s.navn)}
        <button
          class="text-xs px-2.5 py-2 rounded-lg border transition-colors {valg[s.navn]
            ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
            : 'bg-surface-raised text-text-muted border-border'}"
          onclick={() => (valg[s.navn] = !valg[s.navn])}
          aria-pressed={valg[s.navn]}
        >
          {s.navn === 'vann' && valg.vann2 ? '🌊 Venstre skuff' : s.tekst}
        </button>
      {/each}
    </div>
    {#if !harNoenSensor(valg)}
      <p class="text-[11px] text-text-dim mt-1.5">Ingen sensorer: kassa styrer bare lyset.</p>
    {/if}
  </div>

  {#if !erNy && valg.jord.some(Boolean)}
    <!-- Egen tørr/våt for jordsensorene i denne kassa (2.0 stor måler gjennom koppveggen). -->
    <div>
      <span class="label block mb-1">Kalibrering av jordsensorene</span>
      <p class="text-[11px] text-text-dim mb-2 leading-snug">
        Rå tall fra sensorene for tørr og våt jord i denne kassa. Tomt felt = de felles tallene
        ({JORD_TORR} og {JORD_VAT}). Mål tørr med tørr plantejord, og våt når jorda er vannet og har stått en time.
      </p>
      <div class="grid grid-cols-2 gap-2">
        <label class="text-[12px] text-text-muted">
          Tørr (0 %)
          <input
            bind:value={torrTekst}
            inputmode="numeric"
            placeholder={String(JORD_TORR)}
            class="input font-mono mt-1"
            aria-label="Rå ADC for tørr jord"
          />
        </label>
        <label class="text-[12px] text-text-muted">
          Våt (100 %)
          <input
            bind:value={vaatTekst}
            inputmode="numeric"
            placeholder={String(JORD_VAT)}
            class="input font-mono mt-1"
            aria-label="Rå ADC for våt jord"
          />
        </label>
      </div>
      {#if snittNaa !== null}
        <p class="font-mono text-[10.5px] text-text-dim mt-2 leading-snug">
          Nå: {raaNaa.map((x) => `${x.nr}: ${x.v}`).join(' · ')} · snitt {snittNaa}
          ({formaterTidssiden(sisteAvlesning?.registrert_at)})
        </p>
        <div class="flex gap-2 mt-1.5">
          <button class="btn-secondary flex-1 !py-1.5 !px-2 text-xs" onclick={() => (torrTekst = String(snittNaa))}>
            Snittet nå som tørr
          </button>
          <button class="btn-secondary flex-1 !py-1.5 !px-2 text-xs" onclick={() => (vaatTekst = String(snittNaa))}>
            Snittet nå som våt
          </button>
        </div>
      {/if}
      {#if jordFeil}
        <p class="text-[11px] text-rose mt-1.5" role="status">{jordFeil}</p>
      {/if}
    </div>
  {/if}

  {#if erNy && form === 'potte'}
    <div>
      <span class="label block mb-2">Skillevegger</span>
      <div class="flex flex-col gap-2">
        {#each skille as _, i (i)}
          <div class="flex items-center justify-between bg-bg-subtle border border-border rounded-lg px-3 py-2">
            <span class="text-sm">Potte {i + 1}</span>
            <button
              class="text-xs px-2.5 py-1 rounded-full border transition-colors {skille[i]
                ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
                : 'bg-surface-raised text-text-muted border-border'}"
              onclick={() => (skille[i] = !skille[i])}
              aria-pressed={skille[i]}
              aria-label="Skillevegg i potte {i + 1}"
            >
              {skille[i] ? 'Delt (2 felt)' : 'Udelt (1 felt)'}
            </button>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <button class="btn-primary w-full mt-1" onclick={lagre} disabled={lagrer}>
    {lagrer ? 'Lagrer…' : erNy ? 'Opprett kasse' : 'Lagre'}
  </button>
  <button class="btn-secondary w-full" onclick={onLukk}>Avbryt</button>

  {#if !erNy}
    <section class="pt-3 mt-1 border-t border-border">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <div class="text-sm font-medium">{iDrift ? '🌱 I drift' : '🧪 Testmodus'}</div>
          <div class="text-[11px] text-text-muted leading-snug">
            {iDrift
              ? 'Høsting og næring varsles, og fjernede planter havner i historikken.'
              : 'For bygging og testing. Varsler om lys, næring og høsting er av.'}
          </div>
        </div>
        {#if !bekreftModus}
          <button class="btn-secondary !py-1.5 !px-3 text-xs shrink-0" onclick={() => (bekreftModus = true)}>
            {iDrift ? 'Sett i testmodus' : 'Sett i drift'}
          </button>
        {/if}
      </div>
      {#if bekreftModus}
        <div class="mt-2.5 p-3 rounded-lg border border-sun/30 bg-sun/[0.06] text-[12px] leading-snug">
          {#if iDrift}
            Ingenting slettes eller nullstilles. Men så lenge kassa står i testmodus, slettes planter du
            fjerner helt, og varsler om lys, næring og høsting er av.
          {:else}
            Ingenting nullstilles, og plantedatoene beholdes. Fra nå havner planter du fjerner i
            historikken. Skal plantene telle fra i dag, bruk «Ny runde» under.
          {/if}
          <div class="flex gap-2.5 mt-2.5">
            <button class="btn-secondary flex-1" onclick={() => (bekreftModus = false)}>Avbryt</button>
            <button class="btn-primary flex-1" onclick={byttModus} disabled={byttermodus}>
              {byttermodus ? 'Bytter…' : iDrift ? 'Sett i testmodus' : 'Sett i drift'}
            </button>
          </div>
        </div>
      {/if}
      {#if !iDrift && kasse}
        <TestSimulator potteId={kasse.potte_id} />
      {/if}
    </section>

    {#if kasse}
      <section class="pt-3 mt-1 border-t border-border">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="text-sm font-medium">🔄 Ny runde</div>
            <div class="text-[11px] text-text-muted leading-snug">
              Plantene begynner å telle fra i dag, f.eks. når du sår på nytt.
            </div>
          </div>
          {#if !bekreftRunde}
            <button class="btn-secondary !py-1.5 !px-3 text-xs shrink-0" onclick={() => (bekreftRunde = true)}>
              Ny runde
            </button>
          {/if}
        </div>
        {#if bekreftRunde}
          <div class="mt-2.5 p-3 rounded-lg border border-sun/30 bg-sun/[0.06] text-[12px] leading-snug">
            {antallPlanter === 1 ? '1 plante' : `${antallPlanter} planter`} får plantedato i dag.
            {iDrift
              ? 'Plantene og bildene følger den gamle runden under «Tidligere runder». Den nye bildetidslinjen starter tom.'
              : 'Bildetidslinjen begynner på nytt. Testplantenes gamle datoer beholdes ikke.'}
            <div class="flex gap-2.5 mt-2.5">
              <button class="btn-secondary flex-1" onclick={() => (bekreftRunde = false)}>Avbryt</button>
              <button class="btn-primary flex-1" onclick={nyRunde} disabled={starterRunde}>
                {starterRunde ? 'Starter…' : 'Start ny runde'}
              </button>
            </div>
          </div>
        {/if}
      </section>
    {/if}

    <div class="pt-2 mt-1 border-t border-border">
      {#if bekreftSlett}
        <!-- Si det som faktisk slettes: også lysplanen og ALL sensorhistorikk. -->
        <div class="text-[12px] text-rose mb-2 text-center leading-snug">
          Sletter kassa, plantene, lysplanen og all sensorhistorikk for «{potteId}». Det kan ikke
          angres. Slå av enheten først, så den ikke fortsetter å sende målinger. Kamerabildene beholdes. Sikker?
        </div>
        <div class="flex gap-2.5">
          <button class="btn-secondary flex-1" onclick={() => (bekreftSlett = false)}>Nei</button>
          <button class="btn-danger flex-1" onclick={slett} disabled={lagrer}>Ja, slett</button>
        </div>
      {:else}
        <button class="text-[12px] text-rose hover:underline w-full text-center" onclick={() => (bekreftSlett = true)}>
          Slett blomsterkasse
        </button>
      {/if}
    </div>
  {/if}
</div>
